import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { CHAT_REACTIONS } from "../../api/chat";
import type { ChatAttachment, ChatMessage, ChatPerson } from "../../api/chat";
import Ic from "../../components/icons.jsx";
import { formatBytes } from "../../lib/format";
import { ChatAvatar } from "./ChatAvatar";
import { dateTimeOf, shortWhen, timeOf } from "./chatFormat";
import { MessageBody } from "./MessageBody";
import { MessageInput } from "./MessageInput";

/** The reactions one click away on a message; the rest are behind the smile. */
const QUICK_REACTIONS = ["👍", "✅", "👀"];

/**
 * One message, in the channel and in a thread alike: its author where a block starts, its text and files, its
 * reactions, its thread's summary, and the tools that float over it.
 */
export function MessageRow({
  message, head, person, online, people, me, mine, canDelete, sending, editing, selected, paletteOpen, peopleList,
  onClick, onReact, onPalette, onEdit, onCancelEdit, onSaveEdit, onDelete, onRetry, onDiscard, pendingFiles,
  onOpenImage, onMediaLoad, onReplies, found = false,
}: {
  message: ChatMessage;
  head: boolean;
  person: ChatPerson | undefined;
  online: boolean;
  people: Map<string, ChatPerson>;
  me: string;
  mine: boolean;
  canDelete: boolean;
  sending: null | "sending" | "failed";
  editing: boolean;
  selected: boolean;
  paletteOpen: boolean;
  peopleList: ChatPerson[];
  onClick: (e: MouseEvent<HTMLDivElement>) => void;
  onReact: (emoji: string) => void;
  onPalette: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (body: string) => void;
  pendingFiles?: File[];
  onOpenImage: (a: ChatAttachment) => void;
  onMediaLoad: () => void;
  /** Opens the message's thread; given only for a message in the channel. */
  onReplies?: () => void;
  /** The message a search led to: it lights up for a moment. */
  found?: boolean;
  onDelete: () => void;
  onRetry: () => void;
  onDiscard: () => void;
}) {
  const { t } = useTranslation();
  const mentionsMe = !mine && message.mentions.includes(me);
  const names = (ids: string[]) =>
    ids.map((id) => (id === me ? t("chat.you", "you") : people.get(id)?.name ?? "?")).join(", ");
  const classes = ["chat-msg"];
  if (head) classes.push("head");
  if (mentionsMe) classes.push("mention");
  if (sending) classes.push(sending);
  if (selected || paletteOpen) classes.push("tools-open");
  if (editing) classes.push("editing");
  if (found) classes.push("found");

  return (
    // The click only brings the tools up on a touch screen; with a keyboard they are reached with Tab.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={classes.join(" ")} data-message={message.id} onClick={onClick}>
      <div className="chat-msg-gutter">
        {head ? (
          <ChatAvatar person={person} id={message.authorId} online={online} />
        ) : (
          <time className="chat-msg-quiet-time" dateTime={message.createdAt} title={dateTimeOf(message.createdAt)}>
            {timeOf(message.createdAt)}
          </time>
        )}
      </div>
      <div className="chat-msg-main">
        {head && (
          <div className="chat-msg-meta">
            <span className="chat-msg-author">{person?.name ?? t("chat.unknownPerson", "former colleague")}</span>
            <time className="chat-msg-time" dateTime={message.createdAt} title={dateTimeOf(message.createdAt)}>
              {timeOf(message.createdAt)}
            </time>
          </div>
        )}
        {editing ? (
          <MessageInput
            variant="edit"
            people={peopleList}
            initialBody={message.body}
            onSubmit={(body) => onSaveEdit(body)}
            onCancel={onCancelEdit}
            focusOnOpen
          />
        ) : (
          <MessageBody
            body={message.body}
            people={people}
            me={me}
            suffix={message.editedAt && (
              <span className="chat-edited" title={dateTimeOf(message.editedAt)}>{t("chat.edited", "(edited)")}</span>
            )}
          />
        )}
        {sending === "sending" && <span className="chat-sending">{t("chat.sending", "Sending…")}</span>}
        {sending === "failed" && (
          <div className="chat-failed" role="alert">
            {t("chat.notSent", "Not sent.")}{" "}
            <button type="button" onClick={onRetry}>{t("chat.retry", "Try again")}</button>
            {" · "}
            <button type="button" onClick={onDiscard}>{t("chat.discard", "Discard")}</button>
          </div>
        )}
        {message.attachments.length > 0 && (
          <Attachments items={message.attachments} onOpenImage={onOpenImage} onLoad={onMediaLoad} />
        )}
        {pendingFiles && pendingFiles.length > 0 && (
          <div className="chat-files">
            {pendingFiles.map((f, i) => (
              <span key={`${f.name}-${i}`} className="chat-file">
                <span className="chat-file-icon">{f.type.startsWith("image/") ? <Ic.image /> : <Ic.pdf />}</span>
                <span className="chat-file-meta"><b>{f.name}</b><small>{formatBytes(f.size)}</small></span>
              </span>
            ))}
          </div>
        )}
        {onReplies && message.replyCount > 0 && (
          <button type="button" className="chat-thread-summary" onClick={onReplies}>
            <span className="chat-faces-stack">
              {message.replyUsers.map((id) => <ChatAvatar key={id} person={people.get(id)} id={id} size="xs" />)}
            </span>
            <b>{t("chat.replies", "{{count}} replies", { count: message.replyCount })}</b>
            {message.lastReplyAt && (
              <span>{t("chat.lastReply", "last {{when}}", { when: shortWhen(message.lastReplyAt, new Date()) })}</span>
            )}
          </button>
        )}
        {message.reactions.length > 0 && (
          <div className="chat-reactions">
            {message.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className={`chat-reaction${r.users.includes(me) ? " mine" : ""}`}
                title={names(r.users)}
                aria-pressed={r.users.includes(me)}
                aria-label={`${r.emoji} ${r.users.length}: ${names(r.users)}`}
                onClick={() => onReact(r.emoji)}
              >
                <span className="chat-reaction-emoji">{r.emoji}</span>
                <span>{r.users.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {!sending && !editing && (
        <div className="chat-tools" role="toolbar" aria-label={t("chat.messageTools", "Message actions")}>
          {QUICK_REACTIONS.map((e) => (
            <button key={e} type="button" className="chat-tool" onClick={() => onReact(e)}
              aria-label={t("chat.reactWith", "React with {{emoji}}", { emoji: e })}>
              {e}
            </button>
          ))}
          <span className="chat-emoji-anchor">
            <button type="button" className="chat-tool chat-palette-toggle" onClick={onPalette} aria-expanded={paletteOpen}
              title={t("chat.addReaction", "Add a reaction")} aria-label={t("chat.addReaction", "Add a reaction")}>
              <Ic.smile />
            </button>
            {paletteOpen && (
              <div className="chat-palette down" role="menu">
                {CHAT_REACTIONS.map((e) => (
                  <button key={e} type="button" role="menuitem" onClick={() => onReact(e)}
                    aria-label={t("chat.reactWith", "React with {{emoji}}", { emoji: e })}>
                    {e}
                  </button>
                ))}
              </div>
            )}
          </span>
          {onReplies && (
            <button type="button" className="chat-tool" onClick={onReplies}
              title={t("chat.replyInThread", "Reply in thread")} aria-label={t("chat.replyInThread", "Reply in thread")}>
              <Ic.chat />
            </button>
          )}
          {mine && (
            <button type="button" className="chat-tool" onClick={onEdit}
              title={t("chat.edit", "Edit")} aria-label={t("chat.edit", "Edit")}>
              <Ic.pencil />
            </button>
          )}
          {canDelete && (
            <button type="button" className="chat-tool danger" onClick={onDelete}
              title={t("chat.delete", "Delete")} aria-label={t("chat.delete", "Delete")}>
              <Ic.trash />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** A message's files: pictures drawn in the conversation (a click enlarges one), PDFs as cards that open them. */
export function Attachments({ items, onOpenImage, onLoad }: {
  items: ChatAttachment[];
  onOpenImage: (a: ChatAttachment) => void;
  onLoad: () => void;
}) {
  const { t } = useTranslation();
  const isPicture = (a: ChatAttachment) => a.contentType.startsWith("image/") && !!a.url;
  const pictures = items.filter(isPicture);
  const documents = items.filter((a) => !isPicture(a));
  return (
    <div className="chat-files">
      {pictures.length > 0 && (
        <div className={`chat-images${pictures.length === 1 ? " one" : ""}`}>
          {pictures.map((a) => (
            <button
              key={a.id}
              type="button"
              className="chat-image"
              onClick={() => onOpenImage(a)}
              aria-label={t("chat.openImage", "Open {{name}}", { name: a.filename })}
            >
              {/* Loading is not an interaction: the list only keeps its place at the bottom. */}
              {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
              <img src={a.url!} alt={a.filename} loading="lazy" onLoad={onLoad} />
            </button>
          ))}
        </div>
      )}
      {documents.map((a) => (
        <a key={a.id} className="chat-file" href={a.url ?? undefined} target="_blank" rel="noopener noreferrer">
          <span className="chat-file-icon"><Ic.pdf /></span>
          <span className="chat-file-meta">
            <b>{a.filename}</b>
            <small>{formatBytes(a.sizeBytes)}{a.contentType === "application/pdf" ? " · PDF" : ""}</small>
          </span>
        </a>
      ))}
    </div>
  );
}

/** A picture enlarged over a dark backdrop; Escape, a click beside it, or the cross closes it. */
export function Lightbox({ file, onClose }: { file: ChatAttachment; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    // A click on the dark backdrop closes the picture, as Escape does.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="chat-lightbox" onMouseDown={onClose}>
      <figure role="dialog" aria-modal="true" aria-label={file.filename}>
        <img src={file.url ?? ""} alt={file.filename} />
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <figcaption onMouseDown={(e) => e.stopPropagation()}>
          <span>{file.filename} · {formatBytes(file.sizeBytes)}</span>
          <a className="btn sm" href={file.url ?? undefined} target="_blank" rel="noopener noreferrer">
            {t("chat.openNewTab", "Open in a new tab")}
          </a>
          <button type="button" className="chat-tool" onClick={onClose} aria-label={t("chat.close", "Close")}>
            <Ic.x />
          </button>
        </figcaption>
      </figure>
    </div>
  );
}
